import type { CopyFupLinkResult } from '../../services/WorkoutAssignmentService';
import type { FupLink, OrchestratorRow } from '../../types/orchestrator';
type FupLinkActionTarget = {
  studentId: number;
  assignmentId: number | null;
  competence: string;
};

export function removeFupLinkFromRow(
  row: OrchestratorRow,
  token: string,
): OrchestratorRow {
  return {
    ...row,
    fup_links: (row.fup_links ?? []).filter((link) => link.token !== token),
  };
}

export function rowMatchesFupLinkActionTarget(
  row: OrchestratorRow,
  params: FupLinkActionTarget,
  resolvedAssignmentId?: number | null,
): boolean {
  if (row.student_id !== params.studentId) {
    return false;
  }

  if (resolvedAssignmentId != null && row.workout_assignment_id === resolvedAssignmentId) {
    return true;
  }

  if (params.assignmentId != null && row.workout_assignment_id === params.assignmentId) {
    return true;
  }

  // Copying the link may create the cycle server-side before the listing has assignment_id.
  if (params.competence && row.competence === params.competence) {
    return true;
  }

  return false;
}

function appendCopyFupLinkResultToRow(
  row: OrchestratorRow,
  result: CopyFupLinkResult,
): OrchestratorRow {
  if (!result.created_at || !Number.isFinite(result.attempt_number) || result.attempt_number <= 0) {
    return row;
  }

  const existingLinks = row.fup_links ?? [];
  const refreshedLink: FupLink = {
    token: result.token,
    attempt_number: result.attempt_number,
    created_at: result.created_at,
    responded_at: null,
  };

  const linksWithoutAttempt = existingLinks.filter(
    (link) => link.attempt_number !== result.attempt_number,
  );

  return {
    ...row,
    workout_assignment_id: row.workout_assignment_id ?? result.assignment_id,
    fup_links: [...linksWithoutAttempt, refreshedLink].sort(
      (left, right) => left.attempt_number - right.attempt_number,
    ),
  };
}

export function mergeCopyFupLinkResultIntoRow(
  row: OrchestratorRow,
  result: CopyFupLinkResult,
): OrchestratorRow {
  const existingLinks = row.fup_links ?? [];
  if (
    result.reused_existing
    && existingLinks.some((link) => link.token === result.token)
  ) {
    return row;
  }

  return appendCopyFupLinkResultToRow(row, result);
}

export function rowMatchesCopyFupLinkTarget(
  row: OrchestratorRow,
  params: FupLinkActionTarget,
  result: CopyFupLinkResult,
): boolean {
  return rowMatchesFupLinkActionTarget(row, params, result.assignment_id);
}
